import { Info, XCircle } from "@phosphor-icons/react";
import CodeMirror from "@uiw/react-codemirror";
import clsx from "clsx";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useWebhookEventsMutations from "../../datahooks/useWebhookEventsMutations";
import WebhookEventsSelect from "./WebhookEventsSelect";

export default function TestWebhook({
  webhookId,
  webhookEvents,
  onBack,
}: {
  webhookId: string;
  webhookEvents: string[];
  onBack: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { testWebhook, isTestingWebhook, testWebhookResult } =
    useWebhookEventsMutations();
  const [selectedEvent, setSelectedEvent] = useState<string>();

  function onTest() {
    testWebhook({ webhookId, event: selectedEvent });
  }

  const codeMirrorProps = {
    height: "236px",
    theme: "dark",
    basicSetup: { lineNumbers: false, highlightActiveLine: false },
    editable: false,
  } as const;

  let isSuccess = false;
  let feedbackMessage: string;
  if (testWebhookResult) {
    if (typeof testWebhookResult.response.status === "number") {
      isSuccess =
        testWebhookResult.response.status >= 200 &&
        testWebhookResult.response.status < 300;
      feedbackMessage = isSuccess
        ? "Successfully received OK response from the webhook"
        : `Request failed with status code ${testWebhookResult.response.status}`;
    } else {
      feedbackMessage = "Request failed, endpoint doesn't exist";
    }
  }

  const testResultComponent = testWebhookResult && (
    <>
      <div
        className={clsx(
          "mb-4 flex gap-x-2 gap-y-3 rounded-3xl px-4 py-3 text-xs mt-4 lg:mt-6",
          isSuccess ? "bg-green-200" : "bg-red-200",
        )}
      >
        {isSuccess ? (
          <Info size={16} weight="fill" />
        ) : (
          <XCircle size={16} weight="fill" />
        )}
        {feedbackMessage}
      </div>
      <span className="mb-2 text-sm-bold text-black-700">Request sent</span>
      <CodeMirror
        value={`Headers\n${JSON.stringify(testWebhookResult.request.headers, null, 2)}\n\nBody\n${JSON.stringify(testWebhookResult.request.body, null, 2)}`}
        {...codeMirrorProps}
      />
      <span className="mb-2 mt-4 text-sm-bold text-black-700">
        Response received
      </span>
      <CodeMirror
        value={`Headers\n${JSON.stringify(testWebhookResult.response.headers, null, 2)}\n\nBody\n${JSON.stringify(testWebhookResult.response.body, null, 2)}`}
        {...codeMirrorProps}
      />
    </>
  );

  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  const Body = isDesktop ? DialogBody : DrawerBody;

  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <>
      <Header title="Test your webhook" />
      <Body>
        <FormField label="Events" htmlFor="webhook-events-select">
          <WebhookEventsSelect
            events={webhookEvents}
            selection={selectedEvent}
            setSelection={(newEvent) => setSelectedEvent(newEvent)}
            isMultiSelect={false}
          />
        </FormField>
        {!isDesktop && testResultComponent}
      </Body>
      <Footer className="justify-end">
        <Button
          variant="secondary-black"
          onClick={onBack}
          disabled={isTestingWebhook}
        >
          Cancel
        </Button>
        <Button
          disabled={!selectedEvent}
          isLoading={isTestingWebhook}
          onClick={onTest}
        >
          Test Webhook
        </Button>
      </Footer>
      <Body>{isDesktop && testResultComponent}</Body>
    </>
  );
}
